import { Injectable } from '@angular/core';

import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, mapTo, shareReplay, switchMap, take } from 'rxjs/operators';
import { ShAbstractHttpService } from '../../core/http/services/http.abstract.service';
import { OrganisationId } from '../../core/organisations/entities/organisation.entity';
import { UserService } from '../../core/users/services/user.service';
import { SettingGroup } from '../entities/setting-group.entity';
import { ApiSetting, Setting, SettingType, StringSetting } from '../entities/setting.entity';

@Injectable({ providedIn: 'root' })
export class SettingsService {
    private readonly settingsEndpoint: string = `v2/settings`;

    private settings$: Observable<Setting[]>;

    private readonly settingsGroups$: Observable<SettingGroup[]>;

    private readonly organisationId$: Observable<OrganisationId>;

    constructor(
        private readonly httpService: ShAbstractHttpService,
        userService: UserService
    ) {
        this.organisationId$ = userService.getCurrentUser().pipe(map(user => user.Organisation.Id));
        this.settingsGroups$ = this.fetchSettingGroups().pipe(shareReplay(1));
    }

    public get(settingKey: string): Observable<Setting | undefined> {
        return this.getAll().pipe(map(settings => settings.find(setting => setting.Key === settingKey)));
    }

    public getSettingGroups(): Observable<SettingGroup[]> {
        return this.settingsGroups$;
    }

    public getSettingGroupByKey(key: string): Observable<SettingGroup> {
        return this.httpService.get<SettingGroup>(`${this.settingsEndpoint}/${key}`).pipe(shareReplay(1));
    }

    private fetchSettingGroups(): Observable<SettingGroup[]> {
        return this.httpService.get(this.settingsEndpoint);
    }

    protected initialize(): Observable<Setting[]> {
        const organisationSettings$ = this.organisationId$.pipe(
            switchMap(organisationId => this.getSettingGroupByKey(organisationId.toString())),
            map(group => group.Settings),
            catchError(error => {
                console.error('SettingsService:: Unable to retrieve organisation settings', error);
                return of([]);
            })
        );

        const systemSettings$ = this.getSettingGroupByKey('GLOBAL').pipe(
            map(group => group.Settings),
            catchError(error => {
                console.error('SettingsService:: Unable to retrieve GLOBAL settings', error);
                return of([]);
            })
        );

        this.settings$ = combineLatest([organisationSettings$, systemSettings$]).pipe(
            map(([organisationSettings, systemSettings]) =>
                [...organisationSettings, ...systemSettings].map(this.parseValue)
            ),
            shareReplay(1)
        );

        return this.settings$.pipe(take(1));
    }

    private parseValue(setting: ApiSetting): Setting {
        switch (setting.Type) {
            case SettingType.BOOLEAN:
                return {
                    ...setting,
                    Type: setting.Type,
                    Value: setting.Value.toUpperCase() === 'TRUE'
                };
            case SettingType.INTEGER:
                return {
                    ...setting,
                    Type: setting.Type,
                    Value: Number.parseInt(setting.Value, 10)
                };
            case SettingType.FLOAT:
                return {
                    ...setting,
                    Type: setting.Type,
                    Value: Number.parseFloat(setting.Value)
                };
            case SettingType.JSON:
                return {
                    ...setting,
                    Type: setting.Type,
                    Value: setting.Value ? JSON.parse(setting.Value) : '{}'
                };
            case SettingType.URL:
            case SettingType.STRING:
            case SettingType.MULTILINE:
            case SettingType.PASSWORD:
            default:
                return {
                    ...setting
                } as StringSetting;
        }
    }

    public getAll(): Observable<Setting[]> {
        if (typeof this.settings$ === 'undefined') {
            return this.initialize();
        }
        return this.settings$;
    }

    public updateSetting(groupKey: string, settingKey: string, value: string): Observable<boolean> {
        return this.httpService
            .put(`${this.settingsEndpoint}/${groupKey}/${settingKey}`, { Value: value })
            .pipe(mapTo(true));
    }
}
